import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { RootState } from "../../../store/store";
import { usePrincipalForm } from "./hooks/usePrincipalForm";
import { validatePrincipalForm, scrollToFirstError } from "./validation";
import PrincipalFormHeader from "./components/PrincipalFormHeader";
import ErrorDisplay from "./components/ErrorDisplay";
import BasicInformationForm from "./components/BasicInformationForm";
import CommissionInformationForm from "./components/CommissionInformationForm";
import VoucherLocationForm from "./components/VoucherLocationForm";
import AdditionalSettingsForm from "./components/AdditionalSettingsForm";
import BankDetailsForm from "./components/BankDetailsForm";
import ContactInformationForm from "./components/ContactInformationForm";
import WhitelistedIPsForm from "./components/WhitelistedIPsForm";
import AccountInformationForm from "./components/AccountInformationForm";
import ReviewPrincipalPage from "./ReviewPrincipalPage";
import SelfDistributorForm from "./components/SelfDistributorForm";
import { fetchDistributors } from "../../../features/distributor/DistributorSlice";

interface AddPrincipalPageProps {
  onBack: () => void;
}

const AddPrincipalPage: React.FC<AddPrincipalPageProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const { principalError } = useAppSelector((state: RootState) => state.principal);
  const { distributorList, loading: distributorsLoading } = useAppSelector((state: RootState) => state.distributor);
  const [showReview, setShowReview] = useState(false);

  React.useEffect(() => {
    dispatch(fetchDistributors(null));
  }, [dispatch]);

  const {
    formData,
    formErrors,
    setFormErrors,
    handleChange,
    handleAddCommissionSlab,
    handleRemoveCommissionSlab,
    handleCommissionSlabChange,
    validateCommissionSlabs,
    handleAddIp,
    handleRemoveIp,
    handleAddBankAccount,
    handleRemoveBankAccount,
    handleBankAccountChange,
    handleLogoChange,
    handleContractDocumentChange,
    handleRemoveLogo,
    handleRemoveContractDocument,
    setFormDataValue,
  } = usePrincipalForm();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const { hasError, errors } = validatePrincipalForm(formData, false);

    const commissionSlabs = validateCommissionSlabs();
    if (formData.commissionSlabs.length > 0 && !commissionSlabs) {
      errors.commissionSlabs = "Please fill all commission slab fields with valid values";
    }

    if (hasError || errors.commissionSlabs) {
      setFormErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    // If validation passes, show review page
    setShowReview(true);
  };

  const handleReviewBack = () => {
    setShowReview(false);
  };

  const handleSubmitSuccess = () => {
    onBack();
  };

  if (showReview) {
    return (
      <ReviewPrincipalPage
        formData={formData}
        isEditMode={false}
        onBack={handleReviewBack}
        onSubmitSuccess={handleSubmitSuccess}
      />
    );
  }

  return (
    <div className="bg-white h-full">
      <PrincipalFormHeader title="Add Principal" onBack={onBack} />

      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 space-y-8">
        <ErrorDisplay error={principalError} id="add_PrincipalError" />

        <BasicInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onLogoChange={handleLogoChange}
          onRemoveLogo={handleRemoveLogo}
        />

        <CommissionInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onAddCommissionSlab={handleAddCommissionSlab}
          onRemoveCommissionSlab={handleRemoveCommissionSlab}
          onCommissionSlabChange={handleCommissionSlabChange}
        />

        <VoucherLocationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onContractDocumentChange={handleContractDocumentChange}
          onRemoveContractDocument={handleRemoveContractDocument}
        />

        <ContactInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
        />

        <WhitelistedIPsForm
          formData={formData}
          formErrors={formErrors}
          onAddIp={handleAddIp}
          onRemoveIp={handleRemoveIp}
        />

        <AdditionalSettingsForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
        />

        <BankDetailsForm
          formData={formData}
          formErrors={formErrors}
          onAddBankAccount={handleAddBankAccount}
          onRemoveBankAccount={handleRemoveBankAccount}
          onBankAccountChange={handleBankAccountChange}
          isEditMode={false}
        />

        <AccountInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          isEditMode={false}
        />

        <SelfDistributorForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onDistributorChange={(distributorId) => setFormDataValue({ distributorId })}
          distributors={distributorList || []}
          loading={distributorsLoading}
        />

        {/* Action Buttons */}
        <div className="flex justify-between pb-8">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
          >
            Review & Create
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPrincipalPage;
